import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Loader2, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { getReport, listIntegrations, publishArticle, getPublishStatus } from '../services/api';
import type { GeneratedArticle, Integration, PublishedPlatform } from '../types/v2';

export function PublishPage() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const id = articleId ? parseInt(articleId, 10) : NaN;

  const [article, setArticle]           = useState<GeneratedArticle | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [wpEnabled, setWpEnabled]       = useState(false);
  const [fbEnabled, setFbEnabled]       = useState(false);
  const [publishing, setPublishing]     = useState(false);
  const [published, setPublished]       = useState<PublishedPlatform[]>([]);
  const [errors, setErrors]             = useState<Array<{ platform: string; message: string }>>([]);
  const [loadError, setLoadError]       = useState<string | null>(null);

  useEffect(() => {
    if (isNaN(id)) { setLoadError('ID articolo non valido'); return; }

    Promise.all([getReport(id), listIntegrations()])
      .then(([art, integs]) => {
        setArticle(art);
        setIntegrations(integs);
        const wp = integs.find((i) => i.platform === 'wordpress');
        const fb = integs.find((i) => i.platform === 'facebook');
        if (wp?.active && wp?.has_credentials) setWpEnabled(true);
        if (fb?.active && fb?.has_credentials) setFbEnabled(true);

        // Load current publish status
        return getPublishStatus(id);
      })
      .then((status) => {
        if (status.published) setPublished(status.platforms);
      })
      .catch((err) => setLoadError(err.message));
  }, [id]);

  const hasWp = integrations.some((i) => i.platform === 'wordpress' && i.active && i.has_credentials);
  const hasFb = integrations.some((i) => i.platform === 'facebook'  && i.active && i.has_credentials);

  const handlePublish = async () => {
    if (!wpEnabled && !fbEnabled) return;
    setPublishing(true);
    setErrors([]);
    try {
      const res = await publishArticle(id, { wordpress: wpEnabled, facebook: fbEnabled });
      setPublished((prev) => [...prev, ...res.data.published]);
      if (res.data.errors.length > 0) setErrors(res.data.errors);
    } catch (err) {
      setErrors([{ platform: 'general', message: err instanceof Error ? err.message : 'Errore pubblicazione' }]);
    } finally {
      setPublishing(false);
    }
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-3">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
          <p className="text-sm text-red-400">{loadError}</p>
          <button onClick={() => navigate('/report')} className="text-xs text-blue-400 hover:text-blue-300">
            ← Torna agli articoli
          </button>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Send className="w-5 h-5 text-blue-400" />
        <div>
          <h2 className="text-lg font-semibold">Pubblica articolo</h2>
          <p className="text-xs text-gray-500 mt-0.5 truncate max-w-sm">{article.title}</p>
        </div>
      </div>

      {/* Already published */}
      {published.length > 0 && (
        <div className="space-y-2">
          {published.map((p) => (
            <div key={p.platform} className="flex items-center gap-3 bg-green-900/20 border border-green-800 rounded-xl px-4 py-3">
              <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-green-300 font-medium capitalize">{p.platform} — pubblicato</p>
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-500 hover:text-green-400 flex items-center gap-1 truncate">
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  {p.url}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Target selector */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300">Seleziona destinazioni</h3>

        <TargetCard
          platform="WordPress"
          enabled={wpEnabled}
          onToggle={() => setWpEnabled((v) => !v)}
          configured={hasWp}
          onConfigure={() => navigate('/settings')}
          info={integrations.find((i) => i.platform === 'wordpress')?.test_status === 'ok' ? 'Connessione OK' : undefined}
        />

        <TargetCard
          platform="Facebook"
          enabled={fbEnabled}
          onToggle={() => setFbEnabled((v) => !v)}
          configured={hasFb}
          onConfigure={() => navigate('/settings')}
          info={integrations.find((i) => i.platform === 'facebook')?.test_status === 'ok' ? 'Pagina connessa' : undefined}
        />
      </div>

      {errors.length > 0 && (
        <div className="space-y-2">
          {errors.map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span><span className="font-medium capitalize">{e.platform}</span>: {e.message}</span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handlePublish}
        disabled={(!wpEnabled && !fbEnabled) || publishing}
        className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        {publishing
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Pubblicazione in corso...</>
          : <><Send className="w-4 h-4" /> Pubblica</>}
      </button>

      {/* Article preview */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-300">Anteprima articolo</h3>
        {article.image_url && (
          <img src={article.image_url} alt="Copertina" className="w-full rounded-lg object-cover max-h-48" />
        )}
        <div className="text-xs text-gray-400 leading-relaxed line-clamp-5">
          {article.article.slice(0, 400)}...
        </div>
      </div>
    </div>
  );
}

function TargetCard({
  platform, enabled, onToggle, configured, onConfigure, info,
}: {
  platform: string;
  enabled: boolean;
  onToggle: () => void;
  configured: boolean;
  onConfigure: () => void;
  info?: string;
}) {
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${
      configured ? 'border-gray-700 bg-gray-800' : 'border-gray-800 bg-gray-900/50'
    }`}>
      <div className="flex-1">
        <p className="text-sm font-medium text-white">{platform}</p>
        {configured ? (
          <p className="text-xs text-green-400 mt-0.5">{info ?? 'Configurato'}</p>
        ) : (
          <button onClick={onConfigure} className="text-xs text-yellow-400 hover:text-yellow-300 underline mt-0.5">
            Non configurato — configura in Impostazioni
          </button>
        )}
      </div>
      {configured && (
        <button
          onClick={onToggle}
          className={`w-10 h-5 rounded-full transition-colors relative ${enabled ? 'bg-blue-600' : 'bg-gray-700'}`}
        >
          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      )}
    </div>
  );
}
